import styled from '@emotion/styled';
import React from 'react';
import { Typography, Divider, Avatar } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import MobileWrapper, { FlexColumn, FlexCentered } from '../shared/MobileWrapper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import pxLogo from "../../../assets/images/chain-logo.png";
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';

// Componentes de texto com sombra branca
const WhiteTextWithShadow = styled(Typography)<{ fontSize?: number; fontWeight?: string }>(
  ({ fontSize = 12, fontWeight = 'normal' }) => ({
    fontSize,
    fontWeight,
    color: 'white',
    lineHeight: 1.2,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  })
);

const SemiTransparentWhiteText = styled(Typography)<{ fontSize?: number; marginBottom?: number }>(
  ({ fontSize = 11, marginBottom = 0 }) => ({
    fontSize,
    color: 'rgba(255, 255, 255, 0.95)',
    lineHeight: 1.2,
    marginBottom,
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  })
);

const HeaderSection = styled('div')<{ bgColor: string }>(({ bgColor }) => ({
  width: '100%',
  background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
  padding: '5px 10px 15px 10px',
  position: 'relative',
}));

const TopRow = styled('div')({
  flexDirection: 'row',
  alignItems: 'flex-start',
  display: 'flex',
});

const AppName = styled(WhiteTextWithShadow)({
  fontSize: 11,
  fontWeight: 'bold',
});

const ChainSlogan = styled(SemiTransparentWhiteText)({
  fontSize: 10,
});

const NotificationButton = styled(FlexCentered)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  position: 'absolute',
  right: 5,
  top: 0,
});

const UserGreeting = styled(FlexColumn)({
  marginBottom: 15,
});

const GreetingText = styled(SemiTransparentWhiteText)({
  fontSize: 10,
  marginBottom: 2,
});

const UserName = styled(WhiteTextWithShadow)({
  fontSize: 12,
  fontWeight: 'bold',
});

const PointsSection = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const SwiperContainer = styled('div')({
  flex: 1,
  width: '50%',
  '& .swiper-pagination': {
    position: 'static',
    marginTop: 8,
    display: 'flex',
  },
  '& .swiper-pagination-bullet': {
    backgroundColor: '#61616A', // Cor dos pontos inativos
    opacity: 1,
  },
  '& .swiper-pagination-bullet-active': {
    backgroundColor: '#C7C7CC', // Cor do ponto ativo
  },
});

const PointsInfo = styled(FlexColumn)({});

const PointsLabel = styled(SemiTransparentWhiteText)({
  fontSize: 11,
  marginBottom: 2,
  maxWidth: '120px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const PointsValue = styled(WhiteTextWithShadow)({
  fontSize: 13,
  fontWeight: 'bold',
});

const ExtractButton = styled('div')({
  borderRadius: 20,
  padding: '6px 16px',
  border: '1px solid',
});

const ExtractButtonText = styled(WhiteTextWithShadow)({
  fontSize: 11,
  fontWeight: '500',
});

const AppInfoContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 10,
});

const AppDetails = styled('div')({
  // Container para nome e slogan da chain
});

const GreetingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  margin: '8px 0',
  flexDirection: 'row',
});

const PreviewLabel = styled('div')({
  padding: '10px',
  textAlign: 'center',
  color: '#666',
  fontSize: 11,
});

interface HomeHeaderMobileProps {
  data: any;
}

const HomeHeaderMobile: React.FC<HomeHeaderMobileProps> = ({ data }) => {

  return (
    <MobileWrapper data={data}>
      {(chain) => (
        <>
          <HeaderSection bgColor={chain.appColor}>
            <TopRow>
              <img
                src={chain.logoPath || pxLogo}
                alt={chain.name}
                style={{
                  width: 70,
                  height: 70,
                  objectFit: 'contain',
                  borderRadius: 10,
                }}
              />
              <AppInfoContainer>
                <AppDetails>
                  <AppName>{chain.appNameInsideApp || chain.appName}</AppName>
                  <ChainSlogan>{chain.appSlogan}</ChainSlogan>
                </AppDetails>
                <NotificationButton>
                  <NotificationsIcon sx={{ fontSize: 16, color: chain.appHeaderLabelColor || 'white' }} />
                </NotificationButton>
                <GreetingContainer>
                  <UserGreeting>
                    <GreetingText>Olá,</GreetingText>
                    <UserName style={{ color: chain.appHeaderLabelColor || 'white' }}>Cliente</UserName>
                  </UserGreeting>
                  <img src={pxLogo} alt="Preview" style={{ width: '20px', height: '20px', borderRadius: 5, marginTop: 10 }} />
                </GreetingContainer>
              </AppInfoContainer>
            </TopRow>
            <Divider sx={{ width: '100%', margin: '5px 0', backgroundColor: '#C4C4C4' }} />
            <PointsSection>
              <SwiperContainer>
                <Swiper
                  pagination={{ clickable: true, bulletActiveClass: 'swiper-pagination-bullet-active' }}
                  modules={[Autoplay, Pagination]}
                  slidesPerView={1}
                  spaceBetween={0}
                  loop
                  autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }}
                >
                  <SwiperSlide>
                    <PointsInfo>
                      <PointsLabel>
                        {data?.appLabel?.pointsBalanceLabel || 'Saldo em pontos'}
                      </PointsLabel>
                      <PointsValue>120,00</PointsValue>
                    </PointsInfo>
                  </SwiperSlide>
                  <SwiperSlide>
                    <PointsInfo>
                      <PointsLabel>
                        {data?.appLabel?.cashbackBalanceLabel || 'Saldo cashback'}
                      </PointsLabel>
                      <PointsValue>R$ 20,00</PointsValue>
                    </PointsInfo>
                  </SwiperSlide>
                </Swiper>
              </SwiperContainer>
              <ExtractButton style={{ borderColor: chain.appHeaderLabelColor }}>
                <ExtractButtonText>Extrato</ExtractButtonText>
              </ExtractButton>
            </PointsSection>
          </HeaderSection>
          <div style={{
            height: 20,
            borderRadius: '12px 12px 0 0',
            width: '100%',
            backgroundColor: 'white',
            marginTop: -10,
            zIndex: 1,
          }}></div>
          <PreviewLabel>
            * Preview da tela Home *
          </PreviewLabel>
        </>
      )}
    </MobileWrapper>
  );
};

export default HomeHeaderMobile;